import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { Box, InlineStack, TopBar } from "@medentic/polaris-react";
import type { IconSource } from "@medentic/polaris-react/build/ts/src/types";
import { OrganizationIcon } from "@medentic/polaris-icons";
import { clearTokenData } from "@features/auth/utils";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@shared/types/user";
import { useTranslation } from "react-i18next";
import { ROOTS, USER_ROUTES } from "@app/config/constants";
import PIIToggle from "../PIIToggle";
import LocaleSwitcher from "../LocaleSwitcher";
import { URLLinkBuilder } from "@shared/lib";

// Helper function to open URLs without capturing window in closures
// This prevents React DevTools from encountering cross-origin Window objects
const createOpenUrlHandler = (url: string) => {
  return () => {
    // Access window directly, not through closure
    if (typeof window !== 'undefined' && window.open) {
      try {
        window.open(url, "_blank");
      } catch (e) {
        // Silently fail if window.open is blocked
        console.error("Failed to open URL:", e);
      }
    }
  };
};


export type MenuAction = {
  content: string;
  onAction: () => void;
  icon?: IconSource | (() => React.ReactNode);
  helpText?: string;
  active?: boolean;
  suffix?: ReactNode;
};

export type MenuActionGroup = {
  items: MenuAction[];
};

export type MedenticTopBarProps = {
  onNavigationToggle: () => void;
  
  // User menu configuration
  userMenuName: string;
  userMenuInitials?: string;
  userMenuAvatar?: string;
  userMenuDetail?: string;
  
  // Organization menu items (clinics, event organizers, etc.)
  organizationMenuItems?: MenuAction[];
  
  // All organizations link
  allOrganizationsLabel: string;
  allOrganizationsRoute: string;
  
  // Optional features
  showSearchField?: boolean;
  showLocaleSwitcher?: boolean;
  showPIIToggle?: boolean;
  
  // User profile navigation (if undefined, user profile won't be in menu)
  showUserProfileInMenu?: boolean;
  
  // Custom menu actions (for additional items)
  customMenuActions?: MenuActionGroup[];
};

const MedenticTopBar: React.FC<MedenticTopBarProps> = ({
  onNavigationToggle,
  userMenuName,
  userMenuInitials,
  userMenuAvatar,
  userMenuDetail,
  organizationMenuItems = [],
  allOrganizationsLabel,
  allOrganizationsRoute,
  showSearchField = false,
  showLocaleSwitcher = false,
  showPIIToggle = true,
  showUserProfileInMenu = true,
  customMenuActions = [],
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(["user"]);

  const [userMenuActive, setUserMenuActive] = useState(false);

  const toggleUserMenuActive = useCallback(() => {
    setUserMenuActive((userMenuActive) => !userMenuActive);
  }, []);

  const handleLogout = useCallback(() => {
    clearTokenData();
  }, []);

  // Build the menu actions
  const menuActions: MenuActionGroup[] = useMemo(() => {
    const actions: MenuActionGroup[] = [];

    // Organization items section
    if (organizationMenuItems.length > 0 || allOrganizationsRoute) {
      actions.push({
        items: [
          ...organizationMenuItems,
          {
            content: allOrganizationsLabel,
            onAction: () => navigate(allOrganizationsRoute),
            icon: OrganizationIcon,
          },
        ],
      });
    }

    // Custom menu actions
    if (customMenuActions.length > 0) {
      actions.push(...customMenuActions);
    }

    // Help & Changelog section
    // Use helper function to avoid capturing window in closures
    const helpCenterUrl = URLLinkBuilder.supportHelpCenter(undefined, i18n.language);
    const changelogUrl = URLLinkBuilder.wwwChangelog();
    
    actions.push({
      items: [
        {
          content: t("common.helpCenter"),
          onAction: createOpenUrlHandler(helpCenterUrl),
        },
        {
          content: t("common.changelog"),
          onAction: createOpenUrlHandler(changelogUrl),
        },
      ],
    });

    // User profile & logout section
    const userItems: MenuAction[] = [];
    
    if (showUserProfileInMenu && user) {
      userItems.push({
        content: `${user.firstName} ${user.lastName}`,
        onAction: () => navigate(`${ROOTS.USER}/${USER_ROUTES.PROFILE}`),
        helpText: user.email,
      });
    }
    
    userItems.push({
      content: t("common.logout"),
      onAction: handleLogout,
    });

    actions.push({ items: userItems });

    return actions;
  }, [
    organizationMenuItems,
    allOrganizationsLabel,
    allOrganizationsRoute,
    customMenuActions,
    showUserProfileInMenu,
    user,
    navigate,
    t,
    i18n.language,
    handleLogout,
  ]);

  // Memoize search field to prevent unnecessary re-renders
  const searchFieldMarkup = useMemo(
    () =>
      showSearchField ? (
        <TopBar.SearchField
          onChange={() => {}}
          value=""
          placeholder={t("common.search")}
          showFocusBorder
        />
      ) : undefined,
    [showSearchField, t]
  );

  // Build secondary menu
  const secondaryMenuMarkup = useMemo(() => {
    const items: ReactNode[] = [];
    
    if (showPIIToggle) {
      items.push(<PIIToggle key="pii-toggle" />);
    }
    
    if (showLocaleSwitcher) {
      items.push(
        <Box key="locale-switcher">
          <LocaleSwitcher topbarCase={true} />
        </Box>
      );
    }

    return items.length > 0 ? (
      <InlineStack align="center" blockAlign="center" gap="200" wrap={false}>
        {items}
      </InlineStack>
    ) : undefined;
  }, [showPIIToggle, showLocaleSwitcher]);

  // Memoize the UserMenu to prevent avatar flashing
  const userMenuMarkup = useMemo(
    () => (
      <TopBar.UserMenu
        actions={menuActions}
        name={userMenuName}
        detail={userMenuDetail}
        initials={userMenuInitials}
        avatar={userMenuAvatar}
        open={userMenuActive}
        onToggle={toggleUserMenuActive}
        customWidth="350px"
      />
    ),
    [
      menuActions,
      userMenuName,
      userMenuDetail,
      userMenuInitials,
      userMenuAvatar,
      userMenuActive,
      toggleUserMenuActive,
    ]
  );

  return (
    <TopBar
      showNavigationToggle
      onNavigationToggle={onNavigationToggle}
      searchField={searchFieldMarkup}
      secondaryMenu={secondaryMenuMarkup}
      userMenu={userMenuMarkup}
    />
  );
};

// Custom comparison function to prevent unnecessary rerenders
const arePropsEqual = (
  prevProps: MedenticTopBarProps,
  nextProps: MedenticTopBarProps
): boolean => {
  // Compare all primitive props
  return (
    prevProps.userMenuName === nextProps.userMenuName &&
    prevProps.userMenuInitials === nextProps.userMenuInitials &&
    prevProps.userMenuAvatar === nextProps.userMenuAvatar &&
    prevProps.userMenuDetail === nextProps.userMenuDetail &&
    prevProps.allOrganizationsLabel === nextProps.allOrganizationsLabel &&
    prevProps.allOrganizationsRoute === nextProps.allOrganizationsRoute &&
    prevProps.showSearchField === nextProps.showSearchField &&
    prevProps.showLocaleSwitcher === nextProps.showLocaleSwitcher &&
    prevProps.showPIIToggle === nextProps.showPIIToggle &&
    prevProps.showUserProfileInMenu === nextProps.showUserProfileInMenu &&
    prevProps.organizationMenuItems?.length === nextProps.organizationMenuItems?.length &&
    prevProps.customMenuActions?.length === nextProps.customMenuActions?.length
  );
};

export default React.memo(MedenticTopBar, arePropsEqual);
